/**
 * Memory Admin Configuration Tab
 *
 * Lists all configurable parameters with their definitions, types, and defaults.
 */
import { Card } from '@personic/shared'
import { useMemoryAdminParameters } from '../../../hooks/useMemoryAdmin'

export function MemoryAdminConfig() {
  const { data, isLoading } = useMemoryAdminParameters()

  if (isLoading) {
    return (
      <Card className="p-6 bg-neutral-900/50 border-neutral-800 animate-pulse">
        <div className="h-48" />
      </Card>
    )
  }

  const categories = data?.categories ?? []
  const parameters = data?.parameters ?? []

  return (
    <div className="space-y-6">
      {categories.map((cat: { key: string; name: string; description: string }) => {
        const catParams = parameters.filter((p: { category: string }) => p.category === cat.key)
        if (catParams.length === 0) return null

        return (
          <Card key={cat.key} className="p-6 bg-neutral-900/50 border-neutral-800">
            <h3 className="text-lg font-semibold text-white mb-1">{cat.name}</h3>
            <p className="text-xs text-neutral-500 mb-4">{cat.description}</p>

            <div className="space-y-3">
              {catParams.map((param: {
                name: string
                display_name: string
                description: string
                type: string
                default: unknown
                min?: number
                max?: number
                allowed_values?: string[]
              }) => (
                <div
                  key={param.name}
                  className="flex justify-between items-start py-2 border-b border-neutral-800 last:border-0"
                >
                  <div className="flex-1">
                    <p className="text-sm text-white">{param.display_name}</p>
                    <p className="text-xs text-neutral-500 mt-0.5">{param.description}</p>
                    <div className="flex gap-2 mt-1">
                      <span className="text-[10px] px-1.5 py-0.5 bg-neutral-800 rounded text-neutral-400">
                        {param.type}
                      </span>
                      {param.min !== undefined && (
                        <span className="text-[10px] px-1.5 py-0.5 bg-neutral-800 rounded text-neutral-400">
                          min: {param.min}
                        </span>
                      )}
                      {param.max !== undefined && (
                        <span className="text-[10px] px-1.5 py-0.5 bg-neutral-800 rounded text-neutral-400">
                          max: {param.max}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-right ml-4">
                    <span className="text-sm font-mono text-purple-400">
                      {typeof param.default === 'boolean'
                        ? param.default ? 'true' : 'false'
                        : String(param.default)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )
      })}
    </div>
  )
}
